import { CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QuestionCardProps {
  id: string;
  label: string;
  description?: string;
  required?: boolean;
  status?: 'idle' | 'answered' | 'flagged' | 'needs_action';
  children: React.ReactNode;
  className?: string;
}

export function QuestionCard({
  id,
  label,
  description,
  required = false,
  status = 'idle',
  children,
  className
}: QuestionCardProps) {
  return (
    <div
      className={cn(
        "relative border rounded-lg p-5 bg-card transition-all",
        status === 'answered' && "bg-green-50/50 dark:bg-green-950/10",
        status === 'needs_action' && "border-l-4 border-l-primary",
        status === 'flagged' && "border-l-4 border-l-destructive",
        "hover:outline hover:outline-1 hover:outline-primary/30",
        className
      )}
      data-question-id={id}
    >
      {/* Question Label */}
      <div className="mb-3">
        <label 
          htmlFor={id}
          className="text-base font-semibold text-foreground leading-relaxed block"
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
        
        {/* Description/Helper Text */}
        {description && (
          <p 
            className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed"
            id={`${id}-description`}
          >
            {description}
          </p>
        )}
      </div>

      {/* Content Area */}
      <div className="space-y-3">
        {children}
      </div>

      {/* Status Indicator */}
      {status === 'answered' && (
        <div className="absolute top-5 right-5">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" aria-label="Answered" />
        </div>
      )}
    </div>
  );
}
